import { useEffect, useState } from "react";
import { ShoppingBag, Euro, Truck, Images, Swords, AlertCircle } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import api, { errMsg } from "@/lib/api";
import { toast } from "@/components/ui/sonner";

function StatCard({ icon: Icon, label, value, accent = false, testid }) {
  return (
    <div className="card-tactical p-5" data-testid={testid}>
      <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-widest text-slate-400">
        <Icon className={`h-4 w-4 ${accent ? "text-orange-500" : "text-slate-500"}`} /> {label}
      </div>
      <p className={`mt-2 font-heading text-3xl font-black ${accent ? "text-orange-500" : "text-white"}`}>{value}</p>
    </div>
  );
}

export default function OverviewTab({ goTo }) {
  const [data, setData] = useState(null);
  const [sales, setSales] = useState(null);

  useEffect(() => {
    api.get("/admin/overview").then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e)));
    api.get("/admin/sales-stats").then((r) => setSales(r.data)).catch(() => {});
  }, []);

  if (!data) return <p className="text-slate-500">Chargement...</p>;

  const m = data.next_match;

  return (
    <div className="grid gap-6" data-testid="overview-tab">
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatCard icon={ShoppingBag} label="Commandes aujourd'hui" value={data.orders_today} accent testid="overview-orders-today" />
        <StatCard icon={ShoppingBag} label="Commandes totales" value={data.orders_total} testid="overview-orders-total" />
        <StatCard icon={Euro} label="Revenus (payées)" value={`${data.revenue_total.toFixed(2)} €`} accent testid="overview-revenue" />
        <StatCard icon={Truck} label="À préparer / expédier" value={data.orders_to_ship} testid="overview-to-ship" />
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <button onClick={() => goTo?.("fanwall")} data-testid="overview-pending-photos"
          className="card-tactical flex items-center justify-between gap-4 p-5 text-left transition-all hover:border-orange-500/50">
          <div className="flex items-center gap-3">
            <Images className="h-6 w-6 text-orange-500" />
            <div>
              <p className="font-heading text-lg font-bold uppercase">Photos de fans en attente</p>
              <p className="text-xs text-slate-400">À valider pour le mur de fans</p>
            </div>
          </div>
          <span className={`flex h-10 w-10 items-center justify-center rounded-full font-heading text-xl font-black ${data.fan_photos_pending > 0 ? "bg-orange-500 text-[#060B18]" : "bg-slate-700 text-slate-300"}`}>
            {data.fan_photos_pending}
          </span>
        </button>

        {m ? (
          <div className="card-tactical p-5" data-testid="overview-next-match">
            <p className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-widest text-orange-500">
              <Swords className="h-4 w-4" /> Prochain match
            </p>
            <p className="mt-2 font-heading text-lg font-bold uppercase">
              Emisphere <span className="text-orange-500">vs</span> {m.opponent}
            </p>
            <p className="text-xs text-slate-400">{m.game} — {m.event} — {m.date}</p>
          </div>
        ) : (
          <div className="card-tactical flex items-center gap-3 p-5 text-sm text-slate-400" data-testid="overview-next-match-empty">
            <AlertCircle className="h-5 w-5 text-orange-500" /> Aucun match à venir planifié — ajoutez-en un dans l'onglet Matchs.
          </div>
        )}
      </div>

      {sales && (
        <div className="grid gap-4 lg:grid-cols-3" data-testid="sales-stats">
          <div className="card-tactical p-5 lg:col-span-2">
            <p className="mb-3 font-heading text-lg font-bold uppercase">Revenus — 30 derniers jours</p>
            <div className="h-56" data-testid="sales-chart">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={sales.daily}>
                  <CartesianGrid stroke="#1e293b" strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="date" tick={{ fill: "#64748b", fontSize: 10 }} interval={4} />
                  <YAxis tick={{ fill: "#64748b", fontSize: 10 }} width={50} />
                  <Tooltip
                    contentStyle={{ background: "#0B132B", border: "1px solid rgba(255,94,0,0.3)", borderRadius: 8, fontSize: 12 }}
                    labelStyle={{ color: "#f8fafc" }}
                    formatter={(v, name) => (name === "revenue" ? [`${Number(v).toFixed(2)} €`, "Revenus"] : [v, "Commandes"])}
                  />
                  <Bar dataKey="revenue" fill="#FF5E00" radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
          <div className="grid gap-4">
            <div className="card-tactical p-5">
              <p className="mb-3 font-heading text-lg font-bold uppercase">Ventes par produit</p>
              {sales.by_product.length === 0 ? (
                <p className="text-sm text-slate-500">Aucune vente payée pour le moment.</p>
              ) : (
                <div className="grid gap-2">
                  {sales.by_product.map((p) => (
                    <div key={p.name} className="flex items-center justify-between gap-2 text-sm" data-testid={`sales-product-${p.name}`}>
                      <span className="min-w-0 flex-1 truncate text-slate-300">{p.name}</span>
                      <span className="font-mono2 text-xs text-slate-500">×{p.qty}</span>
                      <span className="font-mono2 font-bold text-orange-500">{p.revenue.toFixed(2)} €</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="card-tactical p-5">
              <p className="mb-3 font-heading text-lg font-bold uppercase">Ventes par taille</p>
              {sales.by_size.length === 0 ? (
                <p className="text-sm text-slate-500">—</p>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {sales.by_size.map((s) => (
                    <span key={s.size} data-testid={`sales-size-${s.size}`}
                      className="rounded border border-slate-600 px-2 py-1 font-mono2 text-xs text-slate-300">
                      {s.size} : <span className="font-bold text-orange-400">{s.qty}</span>
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="card-tactical p-5">
        <div className="mb-3 flex items-center justify-between">
          <p className="font-heading text-lg font-bold uppercase">Dernières commandes</p>
          <button onClick={() => goTo?.("orders")} data-testid="overview-goto-orders"
            className="text-xs font-bold uppercase tracking-wider text-orange-400 hover:text-orange-300">
            Tout voir
          </button>
        </div>
        {data.recent_orders.length === 0 ? (
          <p className="text-sm text-slate-500">Aucune commande pour le moment.</p>
        ) : (
          <div className="grid gap-2">
            {data.recent_orders.map((o) => (
              <div key={o.id} data-testid={`overview-order-${o.id}`}
                className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-slate-700/60 bg-[#060B18] px-4 py-2.5 text-sm">
                <span className="font-bold uppercase">{o.product_name}</span>
                <span className="text-xs text-slate-400">{o.customer_name} — {new Date(o.created_at).toLocaleDateString("fr-FR")}</span>
                <span className="font-mono2 font-bold text-orange-500">{o.total.toFixed(2)} €</span>
                <span className={`rounded px-2 py-0.5 text-[10px] font-bold uppercase ${o.payment_status === "paid" ? "bg-emerald-500/15 text-emerald-400" : "bg-orange-500/15 text-orange-400"}`}>
                  {o.status}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
